import React from 'react';
import { useEffect } from 'react';
import type { CSSProperties, FC, MouseEvent, ReactNode } from 'react';
import { cx } from '../../../utils/cx';
import styles from './Modal.module.scss';
import { createPortal } from 'react-dom';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
    ariaLabel?: string;
    overlayClassName?: string;
    className?: string;
    style?: CSSProperties;
    bare?: boolean;
}

export const Modal: FC<ModalProps> = ({
    open,
    onClose,
    children,
    ariaLabel,
    overlayClassName,
    className,
    style,
    bare = false,
}) => {
    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    const handleBackdrop = (event: MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) onClose();
    };

    return createPortal(
        <div className={cx(styles.overlay, overlayClassName)} onMouseDown={handleBackdrop}>
            {bare ? (
                children
            ) : (
                <div
                    className={cx(styles.modal, className)}
                    style={style}
                    role="dialog"
                    aria-modal="true"
                    aria-label={ariaLabel}
                >
                    {children}
                </div>
            )}
        </div>,
        document.body,
    );
};